// Helpers puros da aba "Hub de Fretes" (Transportadoras). Conversões R$ ↔ centavos,
// % ↔ basis points, formulário ↔ documento e validação espelhando o ERPApi
// (domain.ValidateFreightHubConfigDocument), para o usuário ver o erro antes do PUT.

import {
  FREIGHT_HUB_PROVIDER_KEYS,
  FREIGHT_HUB_QUOTE_BUDGET_MAX_SECONDS,
  FreightHubConfigDocument,
  FreightHubProviderConfig,
  FreightHubProviderKey,
  FREIGHT_HUB_LABEL_ROLES,
  FreightHubBox,
  FreightHubLabelRole,
  FreightHubPackingWeek,
  FreightHubServiceLabel,
} from '@/types/freightHubConfig';
import { brlFromCents, centsFromBRL } from './correiosIntegration';

export { brlFromCents, centsFromBRL };

export const PROVIDER_LABELS: Record<FreightHubProviderKey, string> = {
  correios: 'Correios',
  fmtransportes: 'FM Transportes',
  melhorenvio: 'Melhor Envio',
  viverorganizer: 'Viver Organizer (frota própria)',
};

/** 1030 → "10,30"; percentual com 2 casas para o campo. */
export const percentFromBps = (bps: number): string =>
  (bps / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "10,30" → 1030; "1" → 100; inválido → NaN. */
export const bpsFromPercent = (input: string): number => {
  const s = input.replace(/%/g, '').trim();
  if (!s) return NaN;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return NaN;
  return Math.round(Number(normalized) * 100);
};

/** "01310-100" → "01310100"; devolve só dígitos. */
export const zipDigits = (s: string): string => s.replace(/\D/g, '');

/** "01310100" → "01310-100" para exibição. */
export const formatZip = (s: string): string => {
  const d = zipDigits(s);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : s;
};

/** 1000 → "01000"; prefixo de 5 dígitos com zeros à esquerda. */
export const prefixLabel = (n: number): string => String(n).padStart(5, '0');

/** Estado do formulário: números editáveis como string, no formato que o usuário digita. */
export interface FreightHubFormState {
  markup_percent: string;
  min_shipping_cost: string;
  packing_days: string;
  declared_value_cap: string;
  excess_surcharge_per_1000: string;
  provider_timeout_seconds: string;
  quote_budget_seconds: string;
  quote_cache_minutes: string;
  config_refresh_seconds: string;
  catalog_refresh_seconds: string;
  breaker_failures: string;
  breaker_open_seconds: string;
  default_company_id: string;
  origin_zip: string;
  packing_strategy: 'legacy' | 'catalog';
  pack_fill_target: string;
  pack_factor_flat_stack: string;
  pack_factor_flat_upright: string;
  pack_factor_rigid: string;
  pack_min_samples: string;
  legacy_fastest_rule: boolean;
  boxes: BoxFormRow[];
  service_labels: LabelFormRow[];
  providers: ProviderFormRow[];
  zip_restrictions: RestrictionFormRow[];
  own_fleet: {
    service_name: string;
    delivery_days: string;
    included_packages: string;
    additional_package_fee: string;
    percent_fee: string;
  };
  own_fleet_ranges: RangeFormRow[];
}

export interface ProviderFormRow {
  provider_key: FreightHubProviderKey;
  display_name: string;
  enabled: boolean;
  enabled_erp: boolean;
  enabled_site: boolean;
  timeout_seconds: string;
  endpoint_url: string;
  quote_cache_minutes: string;
  apply_min_shipping_cost: boolean;
  apply_markup: boolean;
  apply_surcharge: boolean;
  add_packing_days: boolean;
}

export interface RestrictionFormRow {
  /** chave local para o React (id do banco ou negativo para linhas novas) */
  key: number;
  provider_key: FreightHubProviderKey;
  zip_from: string;
  zip_to: string;
  reason: string;
  enabled: boolean;
}

export interface RangeFormRow {
  key: number;
  zip_prefix_from: string;
  zip_prefix_to: string;
  price: string;
  label: string;
  enabled: boolean;
}

export interface BoxFormRow {
  key: number;
  name: string;
  inner_height: string;
  inner_width: string;
  inner_length: string;
  tare: string;
  max_weight: string;
  priority: string;
  enabled: boolean;
}

export interface LabelFormRow {
  key: number;
  provider_key: FreightHubProviderKey | '*';
  role: FreightHubLabelRole;
  /** não é aparado: o sufixo de prazo começa com espaço (" - {days} dias úteis") */
  template: string;
}

export const LABEL_ROLE_LABELS: Record<FreightHubLabelRole, string> = {
  first: 'Primeira opção',
  cheapest: 'Mais barata',
  fastest: 'Mais rápida',
  default: 'Padrão',
  days_suffix: 'Sufixo de prazo',
};

/** Defaults do motor (iguais aos seeds de add_freight_hub_packing.sql). */
export const PACKING_DEFAULTS = {
  pack_fill_target_bps: 8500,
  pack_factor_flat_stack_bps: 1500,
  pack_factor_flat_upright_bps: 10000,
  pack_factor_rigid_bps: 5500,
  pack_min_samples: 3,
};

export const labelProviderName = (key: string): string =>
  key === '*' ? 'Todas' : (PROVIDER_LABELS as Record<string, string>)[key] ?? key;

/** "0,14" → 0.14; "1.84" → 1.84; "29" → 29; inválido/vazio → NaN. */
export const decimalFromInput = (input: string): number => {
  const s = input.trim();
  if (!s) return NaN;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return NaN;
  return Number(normalized);
};

/** 0.14 → "0,14"; 29 → "29" (sem separador de milhar, para o campo). */
export const decimalLabel = (n: number): string =>
  n.toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false });

/** Volume interno da caixa em litros (1 decimal) ou null se as medidas forem inválidas. */
export const boxVolumeLiters = (b: Pick<BoxFormRow, 'inner_height' | 'inner_width' | 'inner_length'>): number | null => {
  const dims = [b.inner_height, b.inner_width, b.inner_length].map(decimalFromInput);
  if (dims.some((d) => Number.isNaN(d) || d <= 0)) return null;
  return Math.round((dims[0] * dims[1] * dims[2]) / 100) / 10;
};

/**
 * Texto que o cliente verá, com a mesma regra do FreightHub (facade/naming.go): rótulo +
 * sufixo de prazo ("*" / days_suffix) com {days} trocado pelo prazo.
 */
export const previewServiceLabel = (labels: LabelFormRow[], template: string, days: number): string => {
  const suffix = labels.find((l) => l.provider_key === '*' && l.role === 'days_suffix')?.template ?? ' - {days} dias úteis';
  return template + suffix.split('{days}').join(String(days));
};

export interface PackingStrategySummary {
  strategy: string;
  orders: number;
  equal: number;
  over: number;
  under: number;
  predicted_boxes: number;
  actual_boxes: number;
  /** % de pedidos com o nº de caixas certo */
  equal_pct: number;
  /** (previstas − reais) ÷ reais, em % */
  box_error_pct: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const STRATEGY_ORDER: Record<string, number> = { legacy: 0, catalog: 1 };

/** Soma as semanas por estratégia (legacy primeiro). */
export const summarizePackingWeeks = (weeks: FreightHubPackingWeek[]): PackingStrategySummary[] => {
  const by = new Map<string, PackingStrategySummary>();
  for (const w of weeks) {
    const acc = by.get(w.strategy) ?? { strategy: w.strategy, orders: 0, equal: 0, over: 0, under: 0, predicted_boxes: 0, actual_boxes: 0, equal_pct: 0, box_error_pct: 0 };
    acc.orders += w.orders;
    acc.equal += w.equal;
    acc.over += w.over;
    acc.under += w.under;
    acc.predicted_boxes += w.predicted_boxes;
    acc.actual_boxes += w.actual_boxes;
    by.set(w.strategy, acc);
  }
  return [...by.values()]
    .map((a) => ({
      ...a,
      equal_pct: a.orders ? round1((a.equal * 100) / a.orders) : 0,
      box_error_pct: a.actual_boxes ? round1(((a.predicted_boxes - a.actual_boxes) * 100) / a.actual_boxes) : 0,
    }))
    .sort((x, y) => (STRATEGY_ORDER[x.strategy] ?? 9) - (STRATEGY_ORDER[y.strategy] ?? 9) || x.strategy.localeCompare(y.strategy));
};

/** Uma linha por semana (mais recente primeiro) com as estratégias lado a lado. */
export const pivotPackingWeeks = (weeks: FreightHubPackingWeek[]): Array<{ week_start: string; byStrategy: Record<string, FreightHubPackingWeek> }> => {
  const by = new Map<string, Record<string, FreightHubPackingWeek>>();
  for (const w of weeks) {
    const row = by.get(w.week_start) ?? {};
    row[w.strategy] = w;
    by.set(w.week_start, row);
  }
  return [...by.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([week_start, byStrategy]) => ({ week_start, byStrategy }));
};

const providerRow = (p: FreightHubProviderConfig): ProviderFormRow => ({
  provider_key: p.provider_key,
  display_name: p.display_name,
  enabled: p.enabled,
  enabled_erp: p.enabled_erp,
  enabled_site: p.enabled_site,
  timeout_seconds: p.timeout_seconds != null ? String(p.timeout_seconds) : '',
  endpoint_url: p.endpoint_url ?? '',
  quote_cache_minutes: p.quote_cache_minutes != null ? String(p.quote_cache_minutes) : '',
  apply_min_shipping_cost: p.apply_min_shipping_cost,
  apply_markup: p.apply_markup,
  apply_surcharge: p.apply_surcharge,
  add_packing_days: p.add_packing_days,
});

/** Documento → formulário. Garante uma linha por transportadora conhecida (na ordem fixa). */
export const formFromDocument = (doc: FreightHubConfigDocument): FreightHubFormState => {
  const byKey = new Map(doc.providers.map((p) => [p.provider_key, p]));
  const providers = FREIGHT_HUB_PROVIDER_KEYS.map((key) => {
    const p = byKey.get(key);
    return p
      ? providerRow(p)
      : {
          provider_key: key,
          display_name: PROVIDER_LABELS[key],
          enabled: false,
          enabled_erp: false,
          enabled_site: false,
          timeout_seconds: '',
          endpoint_url: '',
          quote_cache_minutes: '',
          apply_min_shipping_cost: key !== 'viverorganizer',
          apply_markup: true,
          apply_surcharge: true,
          add_packing_days: key !== 'viverorganizer',
        };
  });
  return {
    markup_percent: percentFromBps(doc.config.markup_bps),
    min_shipping_cost: brlFromCents(doc.config.min_shipping_cost_cents),
    packing_days: String(doc.config.packing_days),
    declared_value_cap: doc.config.declared_value_cap_cents > 0 ? brlFromCents(doc.config.declared_value_cap_cents) : '',
    excess_surcharge_per_1000: brlFromCents(doc.config.excess_surcharge_per_1000_cents),
    provider_timeout_seconds: String(doc.config.provider_timeout_seconds),
    quote_budget_seconds: String(doc.config.quote_budget_seconds),
    quote_cache_minutes: String(doc.config.quote_cache_minutes),
    config_refresh_seconds: String(doc.config.config_refresh_seconds),
    catalog_refresh_seconds: String(doc.config.catalog_refresh_seconds),
    breaker_failures: String(doc.config.breaker_failures),
    breaker_open_seconds: String(doc.config.breaker_open_seconds),
    default_company_id: String(doc.config.default_company_id),
    origin_zip: formatZip(doc.config.origin_zip),
    packing_strategy: doc.config.packing_strategy === 'catalog' ? 'catalog' : 'legacy',
    pack_fill_target: percentFromBps(doc.config.pack_fill_target_bps ?? PACKING_DEFAULTS.pack_fill_target_bps),
    pack_factor_flat_stack: percentFromBps(doc.config.pack_factor_flat_stack_bps ?? PACKING_DEFAULTS.pack_factor_flat_stack_bps),
    pack_factor_flat_upright: percentFromBps(doc.config.pack_factor_flat_upright_bps ?? PACKING_DEFAULTS.pack_factor_flat_upright_bps),
    pack_factor_rigid: percentFromBps(doc.config.pack_factor_rigid_bps ?? PACKING_DEFAULTS.pack_factor_rigid_bps),
    pack_min_samples: String(doc.config.pack_min_samples ?? PACKING_DEFAULTS.pack_min_samples),
    legacy_fastest_rule: doc.config.legacy_fastest_rule ?? true,
    boxes: (doc.boxes ?? []).map((b) => ({
      key: b.id,
      name: b.name,
      inner_height: decimalLabel(b.inner_height_cm),
      inner_width: decimalLabel(b.inner_width_cm),
      inner_length: decimalLabel(b.inner_length_cm),
      tare: decimalLabel(b.tare_kg),
      max_weight: decimalLabel(b.max_weight_kg),
      priority: String(b.priority),
      enabled: b.enabled,
    })),
    service_labels: (doc.service_labels ?? []).map((l, i) => ({ key: -1000 - i, provider_key: l.provider_key, role: l.role, template: l.template })),
    providers,
    zip_restrictions: doc.zip_restrictions.map((r) => ({
      key: r.id,
      provider_key: r.provider_key,
      zip_from: formatZip(r.zip_from),
      zip_to: formatZip(r.zip_to),
      reason: r.reason ?? '',
      enabled: r.enabled,
    })),
    own_fleet: {
      service_name: doc.own_fleet.service_name,
      delivery_days: String(doc.own_fleet.delivery_days),
      included_packages: String(doc.own_fleet.included_packages),
      additional_package_fee: brlFromCents(doc.own_fleet.additional_package_fee_cents),
      percent_fee: percentFromBps(doc.own_fleet.percent_fee_bps),
    },
    own_fleet_ranges: doc.own_fleet_ranges.map((b) => ({
      key: b.id,
      zip_prefix_from: prefixLabel(b.zip_prefix_from),
      zip_prefix_to: prefixLabel(b.zip_prefix_to),
      price: brlFromCents(b.price_cents),
      label: b.label ?? '',
      enabled: b.enabled,
    })),
  };
};

const intField = (value: string, name: string, min: number, max: number, errors: string[]): number => {
  const s = value.trim();
  if (!/^\d+$/.test(s)) {
    errors.push(`${name}: informe um número inteiro`);
    return NaN;
  }
  const n = Number(s);
  if (n < min || n > max) errors.push(`${name}: deve ficar entre ${min} e ${max}`);
  return n;
};

const moneyField = (value: string, name: string, errors: string[], required = true): number => {
  const cents = centsFromBRL(value);
  if (cents == null) {
    if (required) errors.push(`${name}: informe um valor em R$`);
    return 0;
  }
  if (Number.isNaN(cents) || cents < 0) {
    errors.push(`${name}: valor inválido`);
    return NaN;
  }
  return cents;
};

/**
 * Formulário → documento pronto para o PUT. `errors` vazio = pode enviar. As mensagens
 * espelham as do ERPApi; os ids das grades são preservados quando existem.
 */
export const buildFreightHubPayload = (
  form: FreightHubFormState,
): { payload: FreightHubConfigDocument; errors: string[] } => {
  const errors: string[] = [];

  const markup = bpsFromPercent(form.markup_percent);
  if (Number.isNaN(markup) || markup < 0 || markup > 100000) errors.push('Markup: informe um percentual entre 0 e 1000');
  const minCost = moneyField(form.min_shipping_cost, 'Custo mínimo', errors);
  const cap = moneyField(form.declared_value_cap, 'Teto do valor declarado', errors, false);
  const surcharge = moneyField(form.excess_surcharge_per_1000, 'Sobretaxa por R$ 1.000', errors);
  const packingDays = intField(form.packing_days, 'Dias de embalagem', 0, 60, errors);
  const providerTimeout = intField(form.provider_timeout_seconds, 'Timeout por transportadora', 1, 120, errors);
  const budget = intField(form.quote_budget_seconds, 'Orçamento da cotação', 1, FREIGHT_HUB_QUOTE_BUDGET_MAX_SECONDS, errors);
  if (!Number.isNaN(budget) && !Number.isNaN(providerTimeout) && budget < providerTimeout) {
    errors.push('Orçamento da cotação: deve ser maior ou igual ao timeout por transportadora');
  }
  const cacheMin = intField(form.quote_cache_minutes, 'Cache de cotação', 0, 1440, errors);
  const cfgRefresh = intField(form.config_refresh_seconds, 'Recarga da configuração', 10, 86400, errors);
  const catRefresh = intField(form.catalog_refresh_seconds, 'Recarga do catálogo', 30, 86400, errors);
  const brF = intField(form.breaker_failures, 'Falhas para abrir o circuito', 1, 1000, errors);
  const brOpen = intField(form.breaker_open_seconds, 'Tempo de circuito aberto', 1, 3600, errors);
  const company = intField(form.default_company_id, 'Empresa padrão', 1, 2147483647, errors);
  const origin = zipDigits(form.origin_zip);
  if (origin.length !== 8) errors.push('CEP de origem: deve ter 8 dígitos');

  const providers: FreightHubProviderConfig[] = form.providers.map((p) => {
    const name = p.display_name.trim();
    if (!name) errors.push(`${PROVIDER_LABELS[p.provider_key]}: nome de exibição é obrigatório`);
    let timeout: number | null = null;
    if (p.timeout_seconds.trim()) {
      timeout = intField(p.timeout_seconds, `${PROVIDER_LABELS[p.provider_key]}: timeout`, 1, 120, errors);
    }
    let cache: number | null = null;
    if (p.quote_cache_minutes.trim()) {
      cache = intField(p.quote_cache_minutes, `${PROVIDER_LABELS[p.provider_key]}: cache`, 0, 1440, errors);
    }
    const endpoint = p.endpoint_url.trim();
    if (endpoint && !/^https?:\/\//.test(endpoint)) {
      errors.push(`${PROVIDER_LABELS[p.provider_key]}: URL deve começar com http:// ou https://`);
    }
    return {
      provider_key: p.provider_key,
      display_name: name,
      enabled: p.enabled,
      enabled_erp: p.enabled_erp,
      enabled_site: p.enabled_site,
      timeout_seconds: timeout,
      endpoint_url: endpoint || null,
      quote_cache_minutes: cache,
      apply_min_shipping_cost: p.apply_min_shipping_cost,
      apply_markup: p.apply_markup,
      apply_surcharge: p.apply_surcharge,
      add_packing_days: p.add_packing_days,
    };
  });

  const zip_restrictions = form.zip_restrictions.map((r, i) => {
    const from = zipDigits(r.zip_from);
    const to = zipDigits(r.zip_to);
    if (from.length !== 8 || to.length !== 8) errors.push(`Restrição ${i + 1} (${PROVIDER_LABELS[r.provider_key]}): CEPs devem ter 8 dígitos`);
    else if (from > to) errors.push(`Restrição ${i + 1} (${PROVIDER_LABELS[r.provider_key]}): CEP inicial maior que o final`);
    return {
      id: r.key > 0 ? r.key : 0,
      provider_key: r.provider_key,
      zip_from: from,
      zip_to: to,
      reason: r.reason.trim() || null,
      enabled: r.enabled,
    };
  });

  const fleetName = form.own_fleet.service_name.trim();
  if (!fleetName) errors.push('Frota própria: nome do serviço é obrigatório');
  const fleetDays = intField(form.own_fleet.delivery_days, 'Frota própria: prazo', 0, 60, errors);
  const fleetIncluded = intField(form.own_fleet.included_packages, 'Frota própria: volumes inclusos', 0, 100, errors);
  const fleetFee = moneyField(form.own_fleet.additional_package_fee, 'Frota própria: taxa por volume adicional', errors);
  const fleetPct = bpsFromPercent(form.own_fleet.percent_fee);
  if (Number.isNaN(fleetPct) || fleetPct < 0 || fleetPct > 10000) errors.push('Frota própria: percentual sobre a mercadoria entre 0 e 100');

  const own_fleet_ranges = form.own_fleet_ranges.map((b, i) => {
    const from = intField(b.zip_prefix_from, `Faixa ${i + 1}: prefixo inicial`, 0, 99999, errors);
    const to = intField(b.zip_prefix_to, `Faixa ${i + 1}: prefixo final`, 0, 99999, errors);
    if (!Number.isNaN(from) && !Number.isNaN(to) && from > to) errors.push(`Faixa ${i + 1}: prefixo inicial maior que o final`);
    const price = moneyField(b.price, `Faixa ${i + 1}: preço`, errors);
    if (!Number.isNaN(price) && price <= 0) errors.push(`Faixa ${i + 1}: preço deve ser maior que zero`);
    return {
      id: b.key > 0 ? b.key : 0,
      zip_prefix_from: from,
      zip_prefix_to: to,
      price_cents: price,
      label: b.label.trim() || null,
      enabled: b.enabled,
    };
  });
  const overlap = rangesOverlap(own_fleet_ranges.filter((b) => b.enabled).map((b) => [b.zip_prefix_from, b.zip_prefix_to]));
  if (overlap) errors.push(`Faixas ativas da frota se sobrepõem: ${prefixLabel(overlap[0][0])}-${prefixLabel(overlap[0][1])} e ${prefixLabel(overlap[1][0])}-${prefixLabel(overlap[1][1])}`);
  if (own_fleet_ranges.length > 0 && !own_fleet_ranges.some((b) => b.enabled)) {
    errors.push('Frota própria: mantenha ao menos uma faixa ativa (ou desative a transportadora)');
  }

  const fillTarget = bpsFromPercent(form.pack_fill_target);
  if (Number.isNaN(fillTarget) || fillTarget < 1000 || fillTarget > 10000) errors.push('Ocupação alvo da caixa: entre 10 e 100%');
  const packFactor = (value: string, name: string): number => {
    const bps = bpsFromPercent(value);
    if (Number.isNaN(bps) || bps < 100 || bps > 50000) errors.push(`${name}: entre 1 e 500%`);
    return bps;
  };
  const factorStack = packFactor(form.pack_factor_flat_stack, 'Fator item plano empilhável');
  const factorUpright = packFactor(form.pack_factor_flat_upright, 'Fator item plano rígido');
  const factorRigid = packFactor(form.pack_factor_rigid, 'Fator item volumétrico');
  const minSamples = intField(form.pack_min_samples, 'Amostras mínimas por SKU', 1, 1000, errors);

  const boxNames = new Set<string>();
  const boxes: FreightHubBox[] = form.boxes.map((b, i) => {
    const name = b.name.trim();
    const tag = name ? `Caixa ${name}` : `Caixa ${i + 1}`;
    if (!name) errors.push(`Caixa ${i + 1}: nome é obrigatório`);
    else if (name.length > 60) errors.push(`${tag}: nome deve ter no máximo 60 caracteres`);
    else if (boxNames.has(name.toLowerCase())) errors.push(`${tag}: nome repetido`);
    if (name) boxNames.add(name.toLowerCase());
    const h = decimalFromInput(b.inner_height);
    const w = decimalFromInput(b.inner_width);
    const l = decimalFromInput(b.inner_length);
    if ([h, w, l].some((d) => Number.isNaN(d) || d <= 0 || d > 300)) errors.push(`${tag}: medidas internas devem ficar entre 0 e 300 cm`);
    const tare = decimalFromInput(b.tare);
    if (Number.isNaN(tare) || tare > 50) errors.push(`${tag}: tara deve ficar entre 0 e 50 kg`);
    const maxWeight = decimalFromInput(b.max_weight);
    if (Number.isNaN(maxWeight) || maxWeight <= 0 || maxWeight > 500) errors.push(`${tag}: peso máximo deve ficar entre 0 e 500 kg`);
    const priority = intField(b.priority, `${tag}: prioridade`, 0, 1000000, errors);
    return {
      id: b.key > 0 ? b.key : 0,
      name,
      inner_height_cm: h,
      inner_width_cm: w,
      inner_length_cm: l,
      tare_kg: tare,
      max_weight_kg: maxWeight,
      priority,
      enabled: b.enabled,
    };
  });
  if (form.packing_strategy === 'catalog' && !boxes.some((b) => b.enabled)) {
    errors.push('Estratégia catalog: mantenha ao menos uma caixa ativa no catálogo');
  }

  const labelKeys = new Set<string>();
  const service_labels: FreightHubServiceLabel[] = form.service_labels.map((lb, i) => {
    const tag = `Rótulo ${i + 1} (${labelProviderName(lb.provider_key)} / ${LABEL_ROLE_LABELS[lb.role] ?? lb.role})`;
    if (lb.provider_key !== '*' && !FREIGHT_HUB_PROVIDER_KEYS.includes(lb.provider_key)) errors.push(`${tag}: transportadora inválida`);
    if (!FREIGHT_HUB_LABEL_ROLES.includes(lb.role)) errors.push(`${tag}: papel inválido`);
    if (!lb.template.trim()) errors.push(`${tag}: texto é obrigatório`);
    else if (lb.template.length > 100) errors.push(`${tag}: texto deve ter no máximo 100 caracteres`);
    if (lb.role === 'days_suffix' && !lb.template.includes('{days}')) errors.push(`${tag}: o sufixo de prazo precisa conter {days}`);
    const k = `${lb.provider_key}/${lb.role}`;
    if (labelKeys.has(k)) errors.push(`${tag}: já existe um rótulo para essa transportadora e papel`);
    labelKeys.add(k);
    return { provider_key: lb.provider_key, role: lb.role, template: lb.template };
  });

  const payload: FreightHubConfigDocument = {
    config: {
      markup_bps: markup,
      min_shipping_cost_cents: minCost,
      packing_days: packingDays,
      declared_value_cap_cents: cap,
      excess_surcharge_per_1000_cents: surcharge,
      provider_timeout_seconds: providerTimeout,
      quote_budget_seconds: budget,
      quote_cache_minutes: cacheMin,
      config_refresh_seconds: cfgRefresh,
      catalog_refresh_seconds: catRefresh,
      breaker_failures: brF,
      breaker_open_seconds: brOpen,
      default_company_id: company,
      origin_zip: origin,
      packing_strategy: form.packing_strategy,
      pack_fill_target_bps: fillTarget,
      pack_factor_flat_stack_bps: factorStack,
      pack_factor_flat_upright_bps: factorUpright,
      pack_factor_rigid_bps: factorRigid,
      pack_min_samples: minSamples,
      legacy_fastest_rule: form.legacy_fastest_rule,
    },
    providers,
    zip_restrictions,
    own_fleet: {
      service_name: fleetName,
      delivery_days: fleetDays,
      included_packages: fleetIncluded,
      additional_package_fee_cents: fleetFee,
      percent_fee_bps: fleetPct,
    },
    own_fleet_ranges,
    boxes,
    service_labels,
  };
  return { payload, errors };
};

/** Devolve o primeiro par de intervalos [from,to] (inclusivos) que se sobrepõe, ou null. */
export const rangesOverlap = (ranges: Array<[number, number]>): [[number, number], [number, number]] | null => {
  const sorted = ranges
    .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b))
    .slice()
    .sort((x, y) => x[0] - y[0]);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i][0] <= sorted[i - 1][1]) return [sorted[i - 1], sorted[i]];
  }
  return null;
};

/**
 * Simulação da frota própria (mesma fórmula do FreightHub): base da faixa + volumes
 * acima dos inclusos × taxa + % sobre a mercadoria. Devolve centavos ou null (CEP fora).
 */
export const simulateOwnFleet = (
  form: FreightHubFormState,
  zip: string,
  packages: number,
  merchandiseCents: number,
): number | null => {
  const d = zipDigits(zip);
  if (d.length !== 8) return null;
  const prefix = Number(d.slice(0, 5));
  const band = form.own_fleet_ranges.find(
    (b) => b.enabled && prefix >= Number(b.zip_prefix_from) && prefix <= Number(b.zip_prefix_to),
  );
  if (!band) return null;
  const base = centsFromBRL(band.price);
  const fee = centsFromBRL(form.own_fleet.additional_package_fee) ?? 0;
  const included = Number(form.own_fleet.included_packages) || 0;
  const pct = bpsFromPercent(form.own_fleet.percent_fee);
  if (base == null || Number.isNaN(base) || Number.isNaN(fee) || Number.isNaN(pct)) return null;
  let cost = base;
  if (packages > included) cost += (packages - included) * fee;
  if (pct > 0 && merchandiseCents > 0) cost += Math.round((merchandiseCents * pct) / 10000);
  return cost;
};
